/* eslint-disable */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  //   Link,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DeleteIcon } from '../../assets/images/DeleteIcon.svg';
import RefreshIcon from '@mui/icons-material/Refresh';
import useTaskManager from '../../Hooks/TaskManager/useTaskManager';
import useProject from '../../Hooks/Project/useProject';
import { SwalToast } from '../../components/Common';
import DialogTransition from '../../components/Common/Transitions/DialogTransition';
import {
  DropdownItem,
  TransparentDropdown,
} from '../../components/Common/TransparentDropdown';
import { NOTIFICATION_TYPE } from '../../const/CommonConst';

const CLIP_RECT_ZERO = 'rect(0, 0, 0, 0)';

const formatNotificationDate = (createdAt) => {
  const notificationDate = moment(createdAt);

  if (!notificationDate.isValid()) {
    return '';
  }

  const today = moment();

  if (notificationDate.isSame(today, 'day')) {
    return `Today at ${notificationDate.format('h:mm A')}`;
  }
  const tomorrow = moment(today).add(1, 'day');
  if (notificationDate.isSame(tomorrow, 'day')) {
    return `Tomorrow at ${notificationDate.format('h:mm A')}`;
  }
  const yesterday = moment(today).subtract(1, 'day');
  if (notificationDate.isSame(yesterday, 'day')) {
    return `Yesterday at ${notificationDate.format('h:mm A')}`;
  }
  const startOfCurrentWeek = moment().startOf('week');
  if (
    notificationDate.isSameOrAfter(startOfCurrentWeek, 'day') &&
    notificationDate.isBefore(today, 'day')
  ) {
    return `${notificationDate.format('dddd')} at ${notificationDate.format('h:mm A')}`;
  }
  return `${notificationDate.format('MMM D, YYYY')} at ${notificationDate.format('h:mm A')}`;
};
function NotificationDrawer({ open, setOpen }) {
  const {
    fetchInAppNotifications,
    fetchNotificationDetails,
    clearAllNotifications,
    markNotificationAsRead,
    fetchUnseenNotificationCount,
  } = useTaskManager();
  const { getLockArtifact, activateProject, project } = useProject();
  const [readStatus, setReadStatus] = useState('all');

  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [notificationList, setNotificationList] = useState([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [isCleared, setIsCleared] = useState(false);
  const [showClearAllNotificationsModal, setShowClearAllNotificationsModal] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [showMoreLoading, setShowMoreLoading] = useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };
  const onDeleteClick = () => {
    setShowClearAllNotificationsModal(true);
  };
  const onCloseClick = () => {
    setShowClearAllNotificationsModal(false);
  };

  const handleNavigateToRequiredPage = useCallback(
    (notificationId) => {
      setOpen(false);
      fetchNotificationDetails({ notification_id: notificationId }).then(
        (res) => {
          const notificationData = res?.data?.notification?.notification_data;
          if (notificationData?.module === 'UDF') {
            getLockArtifact({
              artifact_id: notificationData?.task_id,
              project_id: notificationData?.project_id,
              artifact_type: 'udf',
            }).then((res) => {
              if (res?.data?.status === 'unlocked') {
                if (
                  project?.active_project?.project_id !==
                  notificationData?.project_id
                ) {
                  activateProject({
                    project_id: notificationData?.project_id,
                  });
                }
                setTimeout(() => {
                  navigate(`/udf-manager/edit/${notificationData?.task_id}`);
                }, 1000);
              } else {
                SwalToast({
                  icon: 'error',
                  title: res?.data?.msg,
                });
              }
            });
          } else if (notificationData?.module === 'PipelineManager') {
            const projectFound = project?.userProjectsList?.find(
              (prj) => prj?.project_id === notificationData?.project_id
            );
            if (projectFound?.project_id) {
              if (
                projectFound?.project_id !== project?.activeProject?.project_id
              ) {
                activateProject({
                  project_id: projectFound?.project_id,
                });
              }
              // Redirect to pipeline list page
              setTimeout(() => {
                navigate(
                  `/pipeline/${notificationData?.task_id?.pipeline_id}/execution/${notificationData?.task_id?.execution_history_id}`
                );
              }, 1000);
            } else {
              SwalToast({
                icon: 'success',
                title: 'The project is not associated with the active user',
              });
            }
          } else {
            navigate(
              `/task-manager/details/${notificationData?.task_id}/${notificationData?.sub_module}`
            );
          }
        }
      );
    },
    [fetchNotificationDetails]
  );
  const calculatePageSize = () => {
    const availableHeight = window.innerHeight - 60; // 100vh - 60px
    const itemsPerPage = Math.floor(availableHeight / 80); // Each item takes 80px
    return itemsPerPage;
  };
  const fetchInAppNotificationsWrapper = useCallback(() => {
    setLoading(true);
    const pageSize = calculatePageSize();
    fetchInAppNotifications(0, pageSize, readStatus).then((res) => {
      if (res?.data) {
        setNotificationList(res?.data);
        setTotalNotifications(res?.headers['x-total-count']);
      } else {
        setNotificationList([]);
        setTotalNotifications(0);
      }
      setLoading(false);
      fetchUnseenNotificationCount();
    });
  }, [fetchInAppNotifications, setNotificationList, setTotalNotifications]);
  useEffect(() => {
    if (open) {
      fetchInAppNotificationsWrapper();
      setPageIndex(0);
    }
  }, [open, readStatus]);

  const handleCloseDrawer = useCallback(() => setOpen(false), [setOpen]);
  useEffect(() => {
    document.addEventListener('CLOSE_MODALS', handleCloseDrawer);
    return () =>
      document.removeEventListener('CLOSE_MODALS', handleCloseDrawer);
  }, [handleCloseDrawer]);
  const handleShowMore = () => {
    setShowMoreLoading(true);
    const nextPageIndex = pageIndex + 1;
    setPageIndex(nextPageIndex);
    fetchInAppNotifications(nextPageIndex, 5, readStatus).then((res) => {
      if (res?.data) {
        setNotificationList((prevList) => [...prevList, ...res.data]);
        setTotalNotifications(res?.headers['x-total-count']);
        fetchUnseenNotificationCount();
      } else {
        setNotificationList([]);
        setTotalNotifications(0);
      }
      setShowMoreLoading(false);
    });
  };
  const handleClearNotification = () => {
    clearAllNotifications().then((res) => {
      SwalToast({
        icon: 'success',
        title: res?.data?.msg,
      });
      setIsCleared(true);
      setShowClearAllNotificationsModal(false);
      fetchInAppNotificationsWrapper();
    });
  };
  const onRefreshClick = () => {
    fetchInAppNotificationsWrapper();
    setPageIndex(0);
  };
  const handleNotificationTypeChange = useCallback(
    (e) => {
      setReadStatus(e.target?.value);
    },
    [setReadStatus]
  );
  const handleNotificationMarkAsRead = useCallback(
    (notification) => {
      markNotificationAsRead({ notification_id: notification?.id }).then(() => {
        fetchInAppNotificationsWrapper();
      });
    },
    [markNotificationAsRead, fetchInAppNotificationsWrapper]
  );
  return (
    <>
      <Drawer
        open={open}
        onClose={toggleDrawer(false)}
        anchor="right"
        PaperProps={{
          sx: {
            width: '451px',
            height: '100vh',
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxShadow: 'none',
          },
        }}
        aria-labelledby="notification-drawer-title"
        aria-modal="false"
        role="dialog"
      >
        <Grid marginTop="70px" overflow="scroll">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p="0px 16px 16px"
          >
            <Box minWidth={50}>
              <div
                id="select-instruction-notification-type"
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  margin: '-1px',
                  padding: '0',
                  overflow: 'hidden',
                  clip: CLIP_RECT_ZERO,
                  border: '0',
                }}
                aria-live="polite"
              >
                On selecting notification type from dropdown, the content will
                get updated below in the data as per the notification type.
              </div>
              <TransparentDropdown
                id="project-type"
                descriptionClass="notification-type-label select-instruction-notification-type"
                select="true"
                value={readStatus}
                onChange={handleNotificationTypeChange}
              >
                {NOTIFICATION_TYPE.map((item) => (
                  <DropdownItem value={item?.value} key={item?.key}>
                    {item?.label}
                  </DropdownItem>
                ))}
              </TransparentDropdown>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <IconButton
                color="primary"
                size="large"
                onClick={onRefreshClick}
                aria-label="Refresh"
                title="Refresh notifications list"
              >
                <RefreshIcon />
              </IconButton>
              <IconButton
                color="primary"
                sx={{
                  display: notificationList.length > 0 ? 'block' : 'none',
                  position: 'relative',
                  top: '2px',
                }}
                onClick={onDeleteClick}
                aria-label="Clear all notifications"
                title="Clear all notifications"
              >
                <DeleteIcon
                  color="primary"
                  sx={{
                    width: '20px',
                    height: '20px',
                  }}
                />
              </IconButton>
              <IconButton
                onClick={toggleDrawer(false)}
                aria-label="Close"
                title="Close"
              >
                <CloseIcon
                  color="primary"
                  sx={{
                    width: '20px',
                    height: '20px',
                  }}
                />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 'calc(100vh - 64px)',
              }}
            >
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <List>
              {notificationList.map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #ccc',
                    padding: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'flex-start',
                    }}
                  >
                    <ListItemText
                      primary={notification?.message}
                      primaryTypographyProps={{ variant: 'body1' }}
                      sx={{ flex: '1 1 auto' }}
                    />
                    <Box
                      onClick={() =>
                        handleNavigateToRequiredPage(notification?.id)
                      }
                      sx={{
                        marginLeft: '15px',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        variant="body4"
                        component="span"
                        sx={{ textDecoration: 'underline' }}
                      >
                        See Details
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2">
                    {formatNotificationDate(notification?.created_at)}
                  </Typography>
                  {!notification?.read_status && (
                    <Button
                      variant="outlined"
                      color="primary"
                      title="Mark as read"
                      aria-label="Mark as read"
                      onClick={() => handleNotificationMarkAsRead(notification)}
                      size="small"
                      sx={{
                        marginTop: '8px',
                        fontSize: '12px',
                        fontWeight: '400',
                        lineHeight: 'normal',
                        height: '25px',
                        padding: '2px 5px',
                      }}
                    >
                      Mark as read
                    </Button>
                  )}
                </ListItem>
              ))}
              {!isCleared &&
              notificationList?.length !== 0 &&
              notificationList?.length < totalNotifications ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    marginTop: '16px',
                    display: showMoreLoading ? 'none' : 'block',
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleShowMore}
                  >
                    Show More
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                  <Typography variant="body2">
                    That's all your notifications from the last 30 days.
                  </Typography>
                </Box>
              )}
              {showMoreLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '16px 0',
                  }}
                >
                  <Typography>Loading...</Typography>
                </Box>
              )}
            </List>
          )}
        </Grid>
      </Drawer>
      {showClearAllNotificationsModal && (
        <Dialog
          open={showClearAllNotificationsModal}
          aria-labelledby="clear-dialog"
          maxWidth="xs"
          fullWidth
          onClose={onCloseClick}
          TransitionComponent={DialogTransition}
        >
          <DialogTitle id="clear-dialog">Clear All Notifications</DialogTitle>
          <IconButton
            color="primary"
            aria-label="Close"
            onClick={onCloseClick}
            size="large"
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{
                    wordWrap: 'break-word',
                    '&:focus-visible': {
                      border: '2px solid',
                      borderColor: 'primary.main',
                    },
                  }}
                  tabIndex={0}
                  role="note"
                  aria-label="Are you sure you want to clear all notifications?"
                >
                  Are you sure you want to clear all notifications?
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              variant="outlined"
              title="No"
              aria-label="No"
              onClick={onCloseClick}
            >
              No
            </Button>
            <Button
              color="primary"
              variant="contained"
              title="Yes"
              aria-label="Yes"
              onClick={handleClearNotification}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default NotificationDrawer;

NotificationDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
    })
  ).isRequired,
};
